<script setup lang="ts">
import { Button, Input } from '@zerohuman/ui'
import { ref } from 'vue'

defineProps<{ modelValue: string }>()
const emit = defineEmits<{ 'update:modelValue': [string] }>()
const error = ref('')
const busy = ref(false)
async function choose(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return
    error.value = ''
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type) || file.size > 5_000_000) {
        error.value = 'Choose a PNG, JPEG, WebP or GIF image smaller than 5 MB.'
        input.value = ''
        return
    }
    busy.value = true
    const url = URL.createObjectURL(file)
    try {
        const image = new Image()
        image.src = url
        await image.decode()
        const scale = Math.min(1, 128 / Math.max(image.naturalWidth, image.naturalHeight))
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
        const context = canvas.getContext('2d')
        if (!context) throw new Error('image_unavailable')
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        let value = canvas.toDataURL('image/png')
        if (value.length > 24_000) {
            canvas.width = Math.max(1, Math.round(canvas.width / 2))
            canvas.height = Math.max(1, Math.round(canvas.height / 2))
            context.drawImage(image, 0, 0, canvas.width, canvas.height)
            value = canvas.toDataURL('image/png')
        }
        if (value.length > 24_000) throw new Error('image_too_large')
        emit('update:modelValue', value)
    } catch {
        error.value = 'This image could not be loaded. Try another image.'
    } finally {
        URL.revokeObjectURL(url)
        busy.value = false
        input.value = ''
    }
}
</script>
<template>
    <div class="space-y-2" data-testid="tool-image-input">
        <label class="grid gap-1 text-sm">Image or logo
            <Input type="file" accept="image/png,image/jpeg,image/webp,image/gif" :disabled="busy" @change="choose" />
        </label>
        <p class="text-muted-foreground text-xs">PNG, JPEG, WebP or GIF, up to 5 MB.</p>
        <div v-if="modelValue" class="flex items-center gap-3">
            <img :src="modelValue" alt="Tool logo preview" class="size-12 object-contain" />
            <Button type="button" size="sm" variant="outline" @click="emit('update:modelValue', '')">Remove image</Button>
        </div>
        <p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
    </div>
</template>
